/**
 * AllianceHQDialog — Manage alliance headquarters: purchase, upgrade,
 * and configure alliance settings.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import type { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import type {
  AllianceHQ,
  AllianceHQTier,
  AllianceLPDistribution,
  LPDistributionMode,
  Chain,
  Stablecoin,
} from '../../shared/common/MonetizationTypes';
import { Colors, Fonts, FontSize, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';
const BORDER_COLOR = Colors.borderBright;
const CARD_COLOR = Colors.bgCard;
const CARD_SELECTED_COLOR = 0x253a66;
const ACCENT_COLOR = 0x00cccc;

const HQ_TIERS: AllianceHQTier[] = ['outpost', 'station', 'fortress', 'citadel'];

const HQ_PRICES: Record<AllianceHQTier, { price: number; members: number; lpMax: number }> = {
  outpost: { price: 100, members: 10, lpMax: 0 },
  station: { price: 500, members: 25, lpMax: 5 },
  fortress: { price: 2000, members: 50, lpMax: 10 },
  citadel: { price: 10000, members: 100, lpMax: 20 },
};

const TIER_LABELS: Record<AllianceHQTier, { label: string; color: string }> = {
  outpost: { label: I18n.t('alliance_hq.outpost'), color: '#88bbff' },
  station: { label: I18n.t('alliance_hq.station'), color: '#00ff88' },
  fortress: { label: I18n.t('alliance_hq.fortress'), color: '#ff8800' },
  citadel: { label: I18n.t('alliance_hq.citadel'), color: '#ff00cc' },
};

const DISTRIBUTION_MODES: LPDistributionMode[] = ['equal_split', 'rank_weighted', 'activity_weighted'];

const DIST_MODE_LABELS: Record<LPDistributionMode, string> = {
  equal_split: I18n.t('alliance_hq.equal_split'),
  rank_weighted: I18n.t('alliance_hq.rank_weighted'),
  activity_weighted: I18n.t('alliance_hq.activity_weighted'),
  custom: I18n.t('alliance_hq.custom'),
};

export class AllianceHQDialog extends Phaser.GameObjects.Container {
  private dw: number;

  private monetization: MMOMonetizationManager;
  private allianceId: number;
  private hq: AllianceHQ | null = null;
  private distributions: AllianceLPDistribution[] = [];

  private selectedChain: Chain = 'solana';
  private selectedStablecoin: Stablecoin = 'USDC';
  private selectedSharePct: number = 0;
  private selectedDistMode: LPDistributionMode = 'equal_split';

  private contentContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, allianceId?: number) {
    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 652;
    const dh = mobile ? 540 : 560;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dw / 2, height / 2 - dh / 2);
    this.dw = dw;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    // Resolve alliance ID from parameter or MMOStateManager
    if (allianceId !== undefined) {
      this.allianceId = allianceId;
    } else {
      const mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);
      const alliance = mmoState.getAlliance();
      this.allianceId = alliance ? alliance.id : 0;
    }

    // Themed dialog chrome (overlay + panel + header + title + close)
    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.alliance_hq'),
      onClose: () => this.close(),
    });

    // Content container (scrollable area)
    this.contentContainer = scene.add.container(15, 58);
    this.add(this.contentContainer);

    // Status text at bottom
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    closeDialog(this.scene, this);
  }

  // ============ Data Loading ============

  private async loadData(): Promise<void> {
    if (!this.allianceId) {
      this.statusText.setText('Join an alliance to access HQ features');
      const noAlliance = this.scene.add.text(this.dw / 2 - 15, 60, 'You are not in an alliance.\nJoin or create an alliance first.', {
        fontSize: FontSize.md,
        color: '#667788',
        fontFamily: Fonts.body,
        align: 'center',
      });
      noAlliance.setOrigin(0.5, 0);
      this.contentContainer.add(noAlliance);
      return;
    }

    this.statusText.setText('Loading...');
    try {
      const [hq, distributions] = await Promise.all([
        this.monetization.fetchAllianceHQ(this.allianceId),
        this.monetization.fetchAllianceDistributions(this.allianceId, 5),
      ]);
      this.hq = hq;
      this.distributions = distributions;

      if (this.hq) {
        this.selectedSharePct = this.hq.lpSharePct;
        this.selectedDistMode = this.hq.distributionMode;
      }

      this.statusText.setText(this.hq ? `HQ: ${TIER_LABELS[this.hq.hqTier].label}` : 'No HQ established');
      this.renderContent();
    } catch (error) {
      console.error('[AllianceHQ] Failed to load data:', error);
      this.statusText.setText('Failed to load HQ data');
    }
  }

  // ============ Content Rendering ============

  private renderContent(): void {
    this.contentContainer.removeAll(true);

    let y = 0;

    if (this.hq && this.hq.status === 'active') {
      y = this.renderHQStatus(y);
      y += 12;
      y = this.renderUpgradeSection(y);
      y += 12;
      y = this.renderLPShareConfig(y);
      y += 12;
      y = this.renderDistributionHistory(y);
    } else {
      y = this.renderNoHQ(y);
      y += 12;
      y = this.renderPurchaseSection(y);
    }

    y += 10;
    y = this.renderChainSelector(y);
  }

  // ============ HQ Status Section ============

  private renderHQStatus(startY: number): number {
    let y = startY;
    const hq = this.hq!;
    const tierInfo = TIER_LABELS[hq.hqTier];
    const priceInfo = HQ_PRICES[hq.hqTier];

    const sectionLabel = this.scene.add.text(0, y, 'Current HQ Status', {
      fontSize: FontSize.md,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(sectionLabel);
    y += 22;

    // Status card
    const card = this.scene.add.container(0, y);
    const cardBg = this.scene.add.rectangle(0, 0, this.dw - 30, 80, CARD_COLOR);
    cardBg.setOrigin(0, 0);
    cardBg.setStrokeStyle(1, BORDER_COLOR);
    card.add(cardBg);

    const tierText = this.scene.add.text(12, 10, `Tier: ${tierInfo.label}`, {
      fontSize: FontSize.md,
      color: tierInfo.color,
      fontFamily: Fonts.heading,
    });
    card.add(tierText);

    const memberText = this.scene.add.text(12, 30, `Member Cap: ${hq.memberCap}`, {
      fontSize: FontSize.sm,
      color: '#aaccff',
      fontFamily: Fonts.body,
    });
    card.add(memberText);

    // LP sharing removed — territory control uses tax system instead

    const modeText = this.scene.add.text(12, 64, `Distribution: ${DIST_MODE_LABELS[hq.distributionMode]}`, {
      fontSize: FontSize.xs,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    card.add(modeText);

    // Cost badge on right
    const costText = this.scene.add.text(this.dw - 50, 10, `$${priceInfo.price}`, {
      fontSize: FontSize.sm,
      color: '#ffd700',
      fontFamily: Fonts.heading,
    });
    costText.setOrigin(1, 0);
    card.add(costText);

    this.contentContainer.add(card);
    y += 88;

    return y;
  }

  // ============ No HQ Prompt ============

  private renderNoHQ(startY: number): number {
    let y = startY;

    const card = this.scene.add.container(0, y);
    const cardBg = this.scene.add.rectangle(0, 0, this.dw - 30, 60, CARD_COLOR);
    cardBg.setOrigin(0, 0);
    cardBg.setStrokeStyle(1, 0x664400);
    card.add(cardBg);

    const icon = this.scene.add.text(12, 12, 'No HQ', {
      fontSize: FontSize.lg,
      color: '#ff8800',
      fontFamily: Fonts.heading,
    });
    card.add(icon);

    const hint = this.scene.add.text(12, 36, 'Purchase a headquarters to unlock territory control and member caps.', {
      fontSize: FontSize.xs,
      color: '#889999',
      fontFamily: Fonts.body,
      wordWrap: { width: this.dw - 60 },
    });
    card.add(hint);

    this.contentContainer.add(card);
    y += 68;

    return y;
  }

  // ============ Purchase Section (No HQ) ============

  private renderPurchaseSection(startY: number): number {
    let y = startY;

    const sectionLabel = this.scene.add.text(0, y, 'Purchase Headquarters', {
      fontSize: FontSize.md,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(sectionLabel);
    y += 22;

    for (const tier of HQ_TIERS) {
      const info = HQ_PRICES[tier];
      const label = TIER_LABELS[tier];
      const btn = this.createTierButton(0, y, tier, label, info, false);
      this.contentContainer.add(btn);
      y += 52;
    }

    return y;
  }

  // ============ Upgrade Section (Has HQ) ============

  private renderUpgradeSection(startY: number): number {
    let y = startY;
    const currentTier = this.hq!.hqTier;
    const currentIdx = HQ_TIERS.indexOf(currentTier);

    // If already citadel, no upgrades available
    if (currentIdx >= HQ_TIERS.length - 1) {
      const maxText = this.scene.add.text(0, y, 'Maximum tier reached.', {
        fontSize: FontSize.sm,
        color: '#889999',
        fontFamily: Fonts.body,
        fontStyle: 'italic',
      });
      this.contentContainer.add(maxText);
      y += 20;
      return y;
    }

    const sectionLabel = this.scene.add.text(0, y, 'Upgrade HQ', {
      fontSize: FontSize.md,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(sectionLabel);
    y += 22;

    // Show only tiers above the current one
    for (let i = currentIdx + 1; i < HQ_TIERS.length; i++) {
      const tier = HQ_TIERS[i];
      const info = HQ_PRICES[tier];
      const label = TIER_LABELS[tier];
      const btn = this.createTierButton(0, y, tier, label, info, true);
      this.contentContainer.add(btn);
      y += 52;
    }

    return y;
  }

  private createTierButton(
    x: number,
    y: number,
    tier: AllianceHQTier,
    label: { label: string; color: string },
    info: { price: number; members: number; lpMax: number },
    isUpgrade: boolean,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const btnWidth = this.dw - 30;
    const bg = this.scene.add.rectangle(0, 0, btnWidth, 46, 0x1a3344);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x336666);
    container.add(bg);

    const nameText = this.scene.add.text(12, 6, label.label, {
      fontSize: FontSize.sm,
      color: label.color,
      fontFamily: Fonts.heading,
    });
    container.add(nameText);

    const detailText = this.scene.add.text(12, 24, `${info.members} members | LP max: ${info.lpMax}%`, {
      fontSize: FontSize.xs,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    container.add(detailText);

    const priceText = this.scene.add.text(btnWidth - 12, 8, `$${info.price.toLocaleString()}`, {
      fontSize: FontSize.sm,
      color: '#ffd700',
      fontFamily: Fonts.heading,
    });
    priceText.setOrigin(1, 0);
    container.add(priceText);

    const actionLabel = isUpgrade ? 'UPGRADE' : 'PURCHASE';
    const actionText = this.scene.add.text(btnWidth - 12, 26, actionLabel, {
      fontSize: FontSize.xs,
      color: '#44ff88',
      fontFamily: Fonts.heading,
    });
    actionText.setOrigin(1, 0);
    container.add(actionText);

    container.setSize(btnWidth, 46);
    container.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnWidth, 46), Phaser.Geom.Rectangle.Contains);
    container.on('pointerover', () => bg.setFillStyle(0x264466));
    container.on('pointerout', () => bg.setFillStyle(0x1a3344));
    container.on('pointerdown', () => {
      if (isUpgrade) {
        this.handleUpgrade(tier);
      } else {
        this.handlePurchase(tier);
      }
    });

    return container;
  }

  // ============ LP Share Configuration ============

  private renderLPShareConfig(startY: number): number {
    // LP sharing removed — territory control uses alliance tax system
    return startY;
    let y = startY;
    const hq = this.hq!;
    const maxPct = HQ_PRICES[hq.hqTier].lpMax;

    // If tier doesn't support LP sharing, show disabled message
    if (maxPct <= 0) {
      const disabledText = this.scene.add.text(0, y, 'LP sharing not available at this tier. Upgrade to Station or higher.', {
        fontSize: FontSize.xs,
        color: '#667788',
        fontFamily: Fonts.body,
        fontStyle: 'italic',
        wordWrap: { width: this.dw - 40 },
      });
      this.contentContainer.add(disabledText);
      y += 24;
      return y;
    }

    const sectionLabel = this.scene.add.text(0, y, 'LP Share Configuration', {
      fontSize: FontSize.md,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(sectionLabel);
    y += 22;

    // Share percentage slider row
    const pctLabel = this.scene.add.text(0, y, 'Share %:', {
      fontSize: FontSize.sm,
      color: '#aaccff',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(pctLabel);

    // Slider track
    const sliderX = 70;
    const sliderWidth = 260;
    const trackBg = this.scene.add.rectangle(sliderX, y + 6, sliderWidth, 8, 0x0a1020);
    trackBg.setOrigin(0, 0);
    trackBg.setStrokeStyle(1, BORDER_COLOR);
    this.contentContainer.add(trackBg);

    // Slider fill
    const fillWidth = maxPct > 0 ? (this.selectedSharePct / maxPct) * sliderWidth : 0;
    const trackFill = this.scene.add.rectangle(sliderX, y + 6, fillWidth, 8, ACCENT_COLOR);
    trackFill.setOrigin(0, 0);
    this.contentContainer.add(trackFill);

    // Slider value text
    const pctValue = this.scene.add.text(sliderX + sliderWidth + 10, y, `${this.selectedSharePct}%`, {
      fontSize: FontSize.sm,
      color: '#44ff88',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(pctValue);

    // Decrement / Increment buttons
    const decBtn = this.createSmallButton(sliderX + sliderWidth + 55, y, '-', () => {
      if (this.selectedSharePct > 0) {
        this.selectedSharePct = Math.max(0, this.selectedSharePct - 1);
        this.renderContent();
      }
    });
    this.contentContainer.add(decBtn);

    const incBtn = this.createSmallButton(sliderX + sliderWidth + 80, y, '+', () => {
      if (this.selectedSharePct < maxPct) {
        this.selectedSharePct = Math.min(maxPct, this.selectedSharePct + 1);
        this.renderContent();
      }
    });
    this.contentContainer.add(incBtn);

    y += 24;

    // Max hint
    const maxHint = this.scene.add.text(sliderX, y, `Max for ${TIER_LABELS[hq.hqTier].label}: ${maxPct}%`, {
      fontSize: FontSize.xs,
      color: '#667788',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(maxHint);
    y += 18;

    // Distribution mode selector
    const modeLabel = this.scene.add.text(0, y, 'Distribution Mode:', {
      fontSize: FontSize.sm,
      color: '#aaccff',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(modeLabel);
    y += 20;

    for (const mode of DISTRIBUTION_MODES) {
      const isSelected = this.selectedDistMode === mode;
      const modeBtn = this.createModeButton(0, y, mode, isSelected);
      this.contentContainer.add(modeBtn);
      y += 28;
    }

    y += 6;

    // Apply button
    const applyBtn = this.createActionButton(0, y, 'Apply LP Settings', 0x006644, () => this.handleConfigureLPShare());
    this.contentContainer.add(applyBtn);
    y += 36;

    return y;
  }

  private createSmallButton(x: number, y: number, label: string, callback: () => void): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, 20, 18, 0x1a3344);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x336666);
    btn.add(bg);

    const text = this.scene.add.text(10, 9, label, {
      fontSize: FontSize.md,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    btn.setSize(20, 18);
    btn.setInteractive(new Phaser.Geom.Rectangle(0, 0, 20, 18), Phaser.Geom.Rectangle.Contains);
    btn.on('pointerover', () => bg.setFillStyle(0x264466));
    btn.on('pointerout', () => bg.setFillStyle(0x1a3344));
    btn.on('pointerdown', callback);

    return btn;
  }

  private createModeButton(x: number, y: number, mode: LPDistributionMode, isSelected: boolean): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const btnWidth = this.dw - 30;
    const bgColor = isSelected ? CARD_SELECTED_COLOR : CARD_COLOR;
    const borderColor = isSelected ? ACCENT_COLOR : BORDER_COLOR;

    const bg = this.scene.add.rectangle(0, 0, btnWidth, 24, bgColor);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, borderColor);
    container.add(bg);

    const indicator = isSelected ? '> ' : '  ';
    const textColor = isSelected ? '#44ff88' : '#aaccff';
    const text = this.scene.add.text(12, 12, `${indicator}${DIST_MODE_LABELS[mode]}`, {
      fontSize: FontSize.xs,
      color: textColor,
      fontFamily: isSelected ? Fonts.heading : Fonts.body,
    });
    text.setOrigin(0, 0.5);
    container.add(text);

    container.setSize(btnWidth, 24);
    container.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnWidth, 24), Phaser.Geom.Rectangle.Contains);
    container.on('pointerover', () => { if (!isSelected) bg.setFillStyle(0x1e3050); });
    container.on('pointerout', () => { if (!isSelected) bg.setFillStyle(CARD_COLOR); });
    container.on('pointerdown', () => {
      this.selectedDistMode = mode;
      this.renderContent();
    });

    return container;
  }

  // ============ Distribution History ============

  private renderDistributionHistory(startY: number): number {
    let y = startY;

    const sectionLabel = this.scene.add.text(0, y, 'Recent LP Distributions', {
      fontSize: FontSize.md,
      color: '#aaccff',
      fontFamily: Fonts.heading,
    });
    this.contentContainer.add(sectionLabel);
    y += 22;

    if (this.distributions.length === 0) {
      const emptyText = this.scene.add.text(0, y, 'No distributions yet.', {
        fontSize: FontSize.xs,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      this.contentContainer.add(emptyText);
      y += 18;
      return y;
    }

    for (const dist of this.distributions) {
      const card = this.scene.add.container(0, y);
      const cardWidth = this.dw - 30;
      const bg = this.scene.add.rectangle(0, 0, cardWidth, 38, CARD_COLOR);
      bg.setOrigin(0, 0);
      bg.setStrokeStyle(1, BORDER_COLOR);
      card.add(bg);

      const dateStr = new Date(dist.distributedAt).toLocaleDateString();
      const dateText = this.scene.add.text(8, 5, dateStr, {
        fontSize: FontSize.xs,
        color: '#889999',
        fontFamily: Fonts.body,
      });
      card.add(dateText);

      const amountText = this.scene.add.text(8, 20, `${dist.totalDistributedIsd.toFixed(2)} $ISD`, {
        fontSize: FontSize.xs,
        color: '#ffd700',
        fontFamily: Fonts.heading,
      });
      card.add(amountText);

      const membersText = this.scene.add.text(cardWidth - 10, 5, `${dist.memberCount} members`, {
        fontSize: FontSize.xs,
        color: '#aaccff',
        fontFamily: Fonts.body,
      });
      membersText.setOrigin(1, 0);
      card.add(membersText);

      const modeText = this.scene.add.text(cardWidth - 10, 20, `${DIST_MODE_LABELS[dist.distributionMode]} @ ${dist.sharePctApplied}%`, {
        fontSize: FontSize.xs,
        color: '#889999',
        fontFamily: Fonts.body,
      });
      modeText.setOrigin(1, 0);
      card.add(modeText);

      this.contentContainer.add(card);
      y += 44;
    }

    return y;
  }

  // ============ Chain / Stablecoin Selector ============

  private renderChainSelector(startY: number): number {
    let y = startY;

    const sectionLabel = this.scene.add.text(0, y, 'Payment:', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.contentContainer.add(sectionLabel);

    const chains: Chain[] = ['solana', 'ethereum', 'bsc', 'galachain'];
    const stablecoins: Stablecoin[] = ['USDC', 'USDT'];

    let cx = 70;
    for (const chain of chains) {
      const isSelected = this.selectedChain === chain;
      const btn = this.createChainChip(cx, y, chain, isSelected, () => {
        this.selectedChain = chain;
        this.renderContent();
      });
      this.contentContainer.add(btn);
      cx += chain.length * 8 + 30;
    }

    cx += 10;
    for (const coin of stablecoins) {
      const isSelected = this.selectedStablecoin === coin;
      const btn = this.createChainChip(cx, y, coin, isSelected, () => {
        this.selectedStablecoin = coin;
        this.renderContent();
      });
      this.contentContainer.add(btn);
      cx += coin.length * 8 + 24;
    }

    y += 22;
    return y;
  }

  private createChainChip(
    x: number,
    y: number,
    label: string,
    isSelected: boolean,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const chipW = label.length * 8 + 16;
    const bgColor = isSelected ? CARD_SELECTED_COLOR : CARD_COLOR;
    const borderC = isSelected ? ACCENT_COLOR : BORDER_COLOR;

    const bg = this.scene.add.rectangle(0, 0, chipW, 18, bgColor);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, borderC);
    container.add(bg);

    const text = this.scene.add.text(chipW / 2, 9, label, {
      fontSize: FontSize.xs,
      color: isSelected ? '#44ff88' : '#aaccff',
      fontFamily: isSelected ? Fonts.heading : Fonts.body,
    });
    text.setOrigin(0.5);
    container.add(text);

    container.setSize(chipW, 18);
    container.setInteractive(new Phaser.Geom.Rectangle(0, 0, chipW, 18), Phaser.Geom.Rectangle.Contains);
    container.on('pointerdown', callback);

    return container;
  }

  // ============ Shared UI Helpers ============

  private createActionButton(
    x: number,
    y: number,
    label: string,
    color: number,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const btnWidth = this.dw - 30;
    const bg = this.scene.add.rectangle(0, 0, btnWidth, 28, color);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x88aacc);
    btn.add(bg);

    const text = this.scene.add.text(btnWidth / 2, 14, label, {
      fontSize: FontSize.sm,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    btn.setSize(btnWidth, 28);
    btn.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnWidth, 28), Phaser.Geom.Rectangle.Contains);
    btn.on('pointerover', () => bg.setFillStyle(color + 0x222222));
    btn.on('pointerout', () => bg.setFillStyle(color));
    btn.on('pointerdown', callback);

    return btn;
  }

  // ============ Actions ============

  private async handlePurchase(tier: AllianceHQTier): Promise<void> {
    this.statusText.setText(`Purchasing ${TIER_LABELS[tier].label}...`);
    try {
      const price = HQ_PRICES[tier].price;

      // Use PaymentFlowManager if available, otherwise fall back to monetization manager
      const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
      let result: { success: boolean; error?: string };

      if (paymentFlow) {
        result = await paymentFlow.executePayment({
          assetType: 'alliance_hq',
          amountUsd: price,
          chain: this.selectedChain,
          stablecoin: this.selectedStablecoin,
          metadata: { allianceId: this.allianceId, hqTier: tier },
        });
      } else {
        result = await this.monetization.purchaseAllianceHQ(
          this.allianceId,
          tier,
          this.selectedChain,
          this.selectedStablecoin,
        );
      }

      if (result.success) {
        this.statusText.setText(`${TIER_LABELS[tier].label} HQ purchased!`);
        this.loadData();
      } else {
        this.statusText.setText(result.error || 'Purchase failed');
      }
    } catch (error) {
      console.error('[AllianceHQ] Purchase failed:', error);
      this.statusText.setText('Purchase failed');
    }
  }

  private async handleUpgrade(newTier: AllianceHQTier): Promise<void> {
    this.statusText.setText(`Upgrading to ${TIER_LABELS[newTier].label}...`);
    try {
      const price = HQ_PRICES[newTier].price;

      // Use PaymentFlowManager if available, otherwise fall back to monetization manager
      const paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
      let result: { success: boolean; error?: string };

      if (paymentFlow) {
        result = await paymentFlow.executePayment({
          assetType: 'alliance_hq',
          amountUsd: price,
          chain: this.selectedChain,
          stablecoin: this.selectedStablecoin,
          metadata: { allianceId: this.allianceId, hqTier: newTier },
        });
      } else {
        result = await this.monetization.upgradeAllianceHQ(
          this.allianceId,
          newTier,
          this.selectedChain,
          this.selectedStablecoin,
        );
      }

      if (result.success) {
        this.statusText.setText(`Upgraded to ${TIER_LABELS[newTier].label}!`);
        this.loadData();
      } else {
        this.statusText.setText(result.error || 'Upgrade failed');
      }
    } catch (error) {
      console.error('[AllianceHQ] Upgrade failed:', error);
      this.statusText.setText('Upgrade failed');
    }
  }

  private async handleConfigureLPShare(): Promise<void> {
    this.statusText.setText('Saving LP share settings...');
    try {
      const result = await this.monetization.configureAllianceLPShare(
        this.allianceId,
        this.selectedSharePct,
        this.selectedDistMode,
      );
      if (result.success) {
        this.statusText.setText('LP share settings updated!');
        this.loadData();
      } else {
        this.statusText.setText(result.error || 'Configure failed');
      }
    } catch (error) {
      console.error('[AllianceHQ] Configure LP share failed:', error);
      this.statusText.setText('Configure failed');
    }
  }
}
